import { FC, useMemo } from 'react';
import { TbBuildingFactory2, TbChartDots2 } from 'react-icons/tb';

import DropdownButtonSelect from '@/shared/components/dropdowns/DropdownButton/DropdownButtonSelect';
import { FinancialValue } from '@/shared/components/financial-values';
import EmptySection from '@/shared/components/layouts/views/EmptySection';
import { dayjs } from '@/utilities';

import { useFinancialOverviewViewPreference } from '../../hooks/useFinancialOverviewViewPreference';
import {
    FinancialOverviewIndustryKpiMetric,
    FinancialOverviewIndustryKpisProps,
    FinancialOverviewSectorKpisProps,
} from '../../types';
import { FinancialMetricFormulaTooltip } from '../FinancialMetricFormula';
import { FinancialOverviewMetricCardGrid } from '../FinancialOverviewMetricCardGrid';
import { FinancialOverviewPanel } from '../FinancialOverviewPanel';

const formatMetricValue = (metric: FinancialOverviewIndustryKpiMetric) => {
    if (metric.format === 'percentage') {
        return new Intl.NumberFormat('en-US', {
            maximumFractionDigits: 1,
            minimumFractionDigits: 1,
            style: 'percent',
        }).format(metric.value);
    }

    if (metric.format === 'days') {
        return `${new Intl.NumberFormat('en-US', { maximumFractionDigits: 1 }).format(metric.value)} days`;
    }

    if (metric.format === 'months') {
        return `${new Intl.NumberFormat('en-US', { maximumFractionDigits: 1 }).format(metric.value)} months`;
    }

    if (metric.format === 'currency') {
        return <FinancialValue value={metric.value} />;
    }

    if (metric.format === 'number') {
        return <FinancialValue kind='number' value={metric.value} />;
    }

    return `${new Intl.NumberFormat('en-US', {
        maximumFractionDigits: 2,
        minimumFractionDigits: 2,
    }).format(metric.value)}x`;
};

interface FinancialOverviewKpisProps extends FinancialOverviewIndustryKpisProps {
    kind: 'industry' | 'sector';
}

const isClassification = (value: unknown): value is string => typeof value === 'string' && value.length > 0;

const FinancialOverviewKpis: FC<FinancialOverviewKpisProps> = ({
    kind,
    preferenceKey = 'default',
    section,
    sections,
}) => {
    const availableSections = useMemo(() => {
        const uniqueSections = new Map(
            [section, ...(sections ?? [])].map((candidate) => [candidate.classification, candidate] as const),
        );

        return [...uniqueSections.values()];
    }, [section, sections]);
    const [savedClassification, setSelectedClassification] = useFinancialOverviewViewPreference(
        preferenceKey,
        `${kind}KpiClassification`,
        section.classification,
        isClassification,
    );
    const selectedSection =
        availableSections.find((candidate) => candidate.classification === savedClassification) ??
        availableSections[0] ??
        section;
    const formattedAsOf =
        selectedSection.asOf && dayjs(selectedSection.asOf).isValid()
            ? dayjs(selectedSection.asOf).format('MMM D, YYYY')
            : null;
    const isSector = kind === 'sector';
    const description = `Review ${isSector ? 'sector-specific' : 'industry-specific'} operating indicators curated for ${selectedSection.classification}${
        formattedAsOf ? ` as of ${formattedAsOf}` : ''
    }.`;
    const Icon = isSector ? TbChartDots2 : TbBuildingFactory2;

    return (
        <FinancialOverviewPanel
            actions={
                availableSections.length > 1 ? (
                    <div style={{ width: '14rem', maxWidth: '100%' }}>
                        <DropdownButtonSelect<string>
                            aria-label={`Select KPI ${kind}`}
                            matchTriggerWidth={false}
                            menuMinWidth='15rem'
                            onChange={(value) => value && setSelectedClassification(value)}
                            optionPageSize={32}
                            options={availableSections.map((candidate) => ({
                                icon: <Icon />,
                                label: candidate.classification,
                                value: candidate.classification,
                            }))}
                            size='small'
                            placement='bottomRight'
                            searchable
                            searchPlaceholder={`Search ${kind} KPI Profiles...`}
                            value={selectedSection.classification}
                        />
                    </div>
                ) : null
            }
            description={description}
            icon={<Icon style={{ verticalAlign: '-0.125rem' }} />}
            title={isSector ? 'Sector KPIs' : 'Industry KPIs'}
        >
            {selectedSection.metrics.length ? (
                <FinancialOverviewMetricCardGrid
                    showDescriptionTooltips
                    items={selectedSection.metrics.map((metric) => ({
                        description: metric.description,
                        key: metric.key,
                        label: metric.label,
                        tooltip: metric.formula ? (
                            <FinancialMetricFormulaTooltip
                                description={metric.description}
                                formula={metric.formula}
                                label={metric.label}
                            />
                        ) : undefined,
                        value: formatMetricValue(metric),
                    }))}
                />
            ) : (
                <EmptySection
                    description={`No ${isSector ? 'sector-specific' : 'industry-specific'} operating indicators are currently available for this company.`}
                    height='14rem'
                    icon={<Icon size='1.25rem' />}
                    label='KPIs Unavailable'
                />
            )}
        </FinancialOverviewPanel>
    );
};

export const FinancialOverviewIndustryKpis: FC<FinancialOverviewIndustryKpisProps> = (props) => (
    <FinancialOverviewKpis {...props} kind='industry' />
);

export const FinancialOverviewSectorKpis: FC<FinancialOverviewSectorKpisProps> = (props) => (
    <FinancialOverviewKpis {...props} kind='sector' />
);
